/**
 * Store Zustand pour la gestion des paramètres de debug par projet.
 *
 * Ce store gère le chargement, la sauvegarde et l'application
 * des paramètres de debug panel spécifiques à chaque projet.
 * Seuls les utilisateurs ADMIN peuvent modifier les paramètres.
 */

import { create } from "zustand";
import * as Cesium from "cesium";
import {
  DebugSettings,
  getDefaultDebugSettings,
  getDebugSettings,
  saveDebugSettings,
  convertToShadowMode,
  convertFromShadowMode,
} from "@/services/debugSettingsService";
import { EtatDiagnosticRendu } from "@/components/basemap-render-diagnostics/types";
import { useAuthStore } from "./authStore";

// État du store
interface DebugSettingsState {
  // Paramètres actuels
  settings: DebugSettings;

  // État de chargement
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Projet actuel
  currentProjectId: string | null;

  // Permissions
  isReadOnly: boolean;
}

// Actions du store
interface DebugSettingsActions {
  // Chargement des paramètres
  loadSettings: (projectId: string) => Promise<DebugSettings>;
  preloadSettings: (projectId: string) => Promise<DebugSettings>;

  // Sauvegarde des paramètres
  saveSettings: (projectId?: string) => Promise<void>;

  // Mise à jour d'un paramètre
  updateSetting: <K extends keyof DebugSettings>(
    key: K,
    value: DebugSettings[K],
  ) => void;

  // Mise à jour multiple
  updateSettings: (partialSettings: Partial<DebugSettings>) => void;

  // Réinitialisation aux valeurs par défaut
  resetToDefaults: () => void;

  // Conversion en EtatDiagnosticRendu
  toEtatDiagnosticRendu: () => EtatDiagnosticRendu;

  // Application depuis un EtatDiagnosticRendu
  fromEtatDiagnosticRendu: (etat: EtatDiagnosticRendu) => void;

  // Nettoyage
  clear: () => void;
}

type DebugSettingsStore = DebugSettingsState & DebugSettingsActions;

// État initial
const initialState: DebugSettingsState = {
  settings: getDefaultDebugSettings(),
  isLoading: false,
  isSaving: false,
  error: null,
  currentProjectId: null,
  isReadOnly: true, // Par défaut, lecture seule jusqu'à vérification du rôle
};

// Debounce timer pour la sauvegarde automatique
let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const SAVE_DEBOUNCE_MS = 1000;

/**
 * Vérifie si l'utilisateur courant est administrateur
 */
function isUserAdmin(): boolean {
  const user = useAuthStore.getState().user;
  return user?.role === "ADMIN";
}

export const useDebugSettingsStore = create<DebugSettingsStore>((set, get) => ({
  ...initialState,

  loadSettings: async (projectId: string) => {
    const isAdmin = isUserAdmin();
    set({
      isLoading: true,
      error: null,
      currentProjectId: projectId,
      isReadOnly: !isAdmin,
    });

    try {
      const settings = await getDebugSettings(projectId);
      set({ settings, isLoading: false });
      console.log(
        `[DebugSettingsStore] Paramètres chargés pour le projet ${projectId} ` +
          `(mode: ${isAdmin ? "lecture/écriture" : "lecture seule"})`,
      );
      return settings;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur inconnue";
      set({ error: message, isLoading: false });
      console.error("[DebugSettingsStore] Erreur lors du chargement:", error);
      return get().settings;
    }
  },

  preloadSettings: async (projectId: string) => {
    return get().loadSettings(projectId);
  },

  saveSettings: async (projectId?: string) => {
    // Ne pas sauvegarder en mode lecture seule
    if (get().isReadOnly) {
      return;
    }

    const targetProjectId = projectId || get().currentProjectId;
    if (!targetProjectId) {
      console.warn("[DebugSettingsStore] Aucun projet sélectionné pour sauvegarder");
      return;
    }

    set({ isSaving: true, error: null });

    try {
      await saveDebugSettings(targetProjectId, get().settings);
      set({ isSaving: false });
      console.log(`[DebugSettingsStore] Paramètres sauvegardés pour le projet ${targetProjectId}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur inconnue";
      set({ error: message, isSaving: false });
      console.error("[DebugSettingsStore] Erreur lors de la sauvegarde:", error);
    }
  },

  updateSetting: <K extends keyof DebugSettings>(key: K, value: DebugSettings[K]) => {
    // Ne pas mettre à jour en mode lecture seule
    if (get().isReadOnly) {
      console.log(`[DebugSettingsStore] Ignoré: mode lecture seule (${key})`);
      return;
    }

    set((state) => ({
      settings: { ...state.settings, [key]: value },
    }));

    // Sauvegarde automatique avec debounce
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
    }
    saveDebounceTimer = setTimeout(() => {
      void get().saveSettings();
    }, SAVE_DEBOUNCE_MS);
  },

  updateSettings: (partialSettings: Partial<DebugSettings>) => {
    // Ne pas mettre à jour en mode lecture seule
    if (get().isReadOnly) {
      console.log("[DebugSettingsStore] Ignoré: mode lecture seule (updateSettings)");
      return;
    }

    set((state) => ({
      settings: { ...state.settings, ...partialSettings },
    }));

    // Sauvegarde automatique avec debounce
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
    }
    saveDebounceTimer = setTimeout(() => {
      void get().saveSettings();
    }, SAVE_DEBOUNCE_MS);
  },

  resetToDefaults: () => {
    // Ne pas réinitialiser en mode lecture seule
    if (get().isReadOnly) {
      console.log("[DebugSettingsStore] Ignoré: mode lecture seule (resetToDefaults)");
      return;
    }

    set({ settings: getDefaultDebugSettings() });
    void get().saveSettings();
  },

  toEtatDiagnosticRendu: (): EtatDiagnosticRendu => {
    const { settings } = get();

    return {
      hdrActif: settings.hdrActif,
      gammaScene: settings.gammaScene,
      brouillardActif: settings.brouillardActif,
      fxaaActif: settings.fxaaActif,
      soleilVisible: settings.soleilVisible,
      atmosphereVisible: settings.atmosphereVisible,
      eclairageGlobeActif: settings.eclairageGlobeActif,
      ombresSceneActives: settings.ombresSceneActives,
      ombresTerrain: convertToShadowMode(settings.ombresTerrain) as Cesium.ShadowMode,
      modeLumiere: settings.modeLumiere,
      intensiteLumiere: settings.intensiteLumiere,
      couleurLumiere: settings.couleurLumiere,
      ombresBasemap: convertToShadowMode(settings.ombresBasemap) as Cesium.ShadowMode,
      backFaceCulling: settings.backFaceCulling,
      debugWireframe: settings.debugWireframe,
      debugColorizeTiles: settings.debugColorizeTiles,
      debugSurfaceAplatie: settings.debugSurfaceAplatie,
      modeShaderBasemap: settings.modeShaderBasemap,
      gainCouleur: settings.gainCouleur,
      facteurIblLitSdr: settings.facteurIblLitSdr,
      intensiteLumiereBasemapLitSdr: settings.intensiteLumiereBasemapLitSdr,
      plancherLumineuxLit: settings.plancherLumineuxLit,
      plancherLumineuxDynamiqueActif: settings.plancherLumineuxDynamiqueActif,
      plancherLumineuxDynamiqueMin: settings.plancherLumineuxDynamiqueMin,
      plancherLumineuxDynamiqueMax: settings.plancherLumineuxDynamiqueMax,
      attenuationNocturneBasemapActive: settings.attenuationNocturneBasemapActive,
      facteurIblBasemapNuit: settings.facteurIblBasemapNuit,
      intensiteBasemapNuit: settings.intensiteBasemapNuit,
      plancherLumineuxBasemapNuit: settings.plancherLumineuxBasemapNuit,
      intensiteLumiereSceneNuit: settings.intensiteLumiereSceneNuit,
      intensiteLumiereArchitecte: settings.intensiteLumiereArchitecte,
      intensiteLumierePaysage: settings.intensiteLumierePaysage,
    };
  },

  fromEtatDiagnosticRendu: (etat: EtatDiagnosticRendu) => {
    // Ne pas synchroniser en mode lecture seule (les changements ne seront pas sauvegardés)
    if (get().isReadOnly) {
      return;
    }

    const settings: Partial<DebugSettings> = {
      hdrActif: etat.hdrActif,
      gammaScene: etat.gammaScene,
      brouillardActif: etat.brouillardActif,
      fxaaActif: etat.fxaaActif,
      soleilVisible: etat.soleilVisible,
      atmosphereVisible: etat.atmosphereVisible,
      eclairageGlobeActif: etat.eclairageGlobeActif,
      ombresSceneActives: etat.ombresSceneActives,
      ombresTerrain: convertFromShadowMode(etat.ombresTerrain),
      modeLumiere: etat.modeLumiere,
      intensiteLumiere: etat.intensiteLumiere,
      couleurLumiere: etat.couleurLumiere,
      ombresBasemap: convertFromShadowMode(etat.ombresBasemap),
      backFaceCulling: etat.backFaceCulling,
      debugWireframe: etat.debugWireframe,
      debugColorizeTiles: etat.debugColorizeTiles,
      debugSurfaceAplatie: etat.debugSurfaceAplatie,
      modeShaderBasemap: etat.modeShaderBasemap,
      gainCouleur: etat.gainCouleur,
      facteurIblLitSdr: etat.facteurIblLitSdr,
      intensiteLumiereBasemapLitSdr: etat.intensiteLumiereBasemapLitSdr,
      plancherLumineuxLit: etat.plancherLumineuxLit,
      plancherLumineuxDynamiqueActif: etat.plancherLumineuxDynamiqueActif,
      plancherLumineuxDynamiqueMin: etat.plancherLumineuxDynamiqueMin,
      plancherLumineuxDynamiqueMax: etat.plancherLumineuxDynamiqueMax,
      attenuationNocturneBasemapActive: etat.attenuationNocturneBasemapActive,
      facteurIblBasemapNuit: etat.facteurIblBasemapNuit,
      intensiteBasemapNuit: etat.intensiteBasemapNuit,
      plancherLumineuxBasemapNuit: etat.plancherLumineuxBasemapNuit,
      intensiteLumiereSceneNuit: etat.intensiteLumiereSceneNuit,
      intensiteLumiereArchitecte: etat.intensiteLumiereArchitecte,
      intensiteLumierePaysage: etat.intensiteLumierePaysage,
    };

    set((state) => ({
      settings: { ...state.settings, ...settings },
    }));

    // Sauvegarde automatique avec debounce
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
    }
    saveDebounceTimer = setTimeout(() => {
      void get().saveSettings();
    }, SAVE_DEBOUNCE_MS);
  },

  clear: () => {
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
      saveDebounceTimer = null;
    }
    set(initialState);
  },
}));
